import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useDispatch } from "react-redux";

import InfiniteScroll from "react-infinite-scroller";
import { pathOr } from "ramda";
import LoadingSpinner from "../../../../components/loadingSpinner";
import BroadcastService from "../../../../service/BroadcastUserService";
import PreviewFile from "../selectFile/components/previewFile";
import SelectFileInner from "../selectFile/components/selectFileInner";
import { setPopup } from "../../../../actions/popupAction";
import ConfirmPopup from "../../../../components/confirmPopup";
import SelectFileMob from "./components/selectFileMob";

const SelectFile = forwardRef((props, ref) => {
  const {
    handleUseCustomFile,
    userType,
    handleLoading,
    editDeleteAccess,
    changePreview,
  } = props;
  const broadcast = new BroadcastService();
  const [state, setState] = useState({
    savedFiles: [],
    previewPopup: false,
    page: 1,
    hasMore: true,
    search: "",
    fileForPreview: {},
    isLoading: false,
    edit: false,
    selectedIndex: -1,
    newFileName: "",
    deleteFile: false,
    fileToDelete: {},
    fileSelectedMob: null,
  });

  const dispatch = useDispatch();
  const actionRef = useRef(false);
  const handlePreview = (file) => {
    setState((st) => ({
      ...st,
      previewPopup: !state.previewPopup,
      fileForPreview: file,
    }));
    changePreview(!state.previewPopup);
  };

  useImperativeHandle(
    ref,
    () => ({
      handleFileSave: (file) => {
        selectFile(file);
      },
      selectedCount: () => {
        return state.selectedIndex;
      },
    }),
    [state]
  );

  const selectFile = (file) => {
    handleUseCustomFile(file);
  };

  const handleOnChange = (e) => {
    const temp = e.target.value;
    setState((st) => ({
      ...st,
      newFileName: temp,
    }));
  };

  const handleEdit = (index) => {
    setState((st) => ({
      ...st,
      edit: true,
      selectedIndex: index,
    }));
  };

  const handleCancel = (file) => {
    setState((st) => ({
      ...st,
      edit: false,
      newFileName: "",
    }));
  };

  const refreshData = async () => {
    try {
      setState((st) => ({ ...st, isLoading: true }));
      const files = await broadcast.getSavedBroadcastFiles(userType, 1);
      if (files?.length > 0) {
        setState((st) => ({
          ...st,
          isLoading: false,
          savedFiles: files,
          page: 2,
        }));
      }
    } catch (err) {
      setState((st) => ({
        ...st,
        isLoading: false,
      }));
    }
  };

  const handleSave = async (item) => {
    if (actionRef && actionRef.current) return;
    else {
      const name = state.newFileName.trim();
      const flg = !/\S/.test(name);
      if (flg) return;
      try {
        actionRef.current = true;
        handleLoading(true);
        const obj = {
          fileNickName: item.fileNickName,
          newFileNickName: name,
        };
        const response = await broadcast.updateSavedFileName(obj, userType);
        setState((st) => ({
          ...st,
          newFileName: "",
          selectedindex: -1,
          edit: false,
        }));
        handleLoading(false);
        refreshData();
        throwMessage("success", response);
        actionRef.current = false;
      } catch (err) {
        handleLoading(false);
        throwMessage("error", err);
        actionRef.current = false;
      }
    }
  };

  const Delete = async (item) => {
    if (actionRef && actionRef.current) return;
    else {
      try {
        actionRef.current = true;
        handleLoading(true);
        const obj = {
          userType,
          fileNickName: item.fileNickName,
        };
        const response = await broadcast.deleteFile(obj);
        setState((st) => ({
          ...st,
          selectedindex: -1,
          deleteFile: false,
        }));
        handleLoading(false);
        refreshData();
        throwMessage("success", response);
        actionRef.current = false;
      } catch (err) {
        handleLoading(false);
        throwMessage("error", err);
        setState((st) => ({ ...st, deleteFile: false }));
        actionRef.current = false;
      }
    }
  };

  const throwMessage = (type, response) => {
    dispatch(
      setPopup({
        customPopupMessage: true,
        messageType: type,
        message:
          type === "success"
            ? response.data.message
            : pathOr(
                "Something went wrong",
                ["response", "data", "message"],
                response
              ),
      })
    );
  };

  const handleDelete = (file) => {
    setState((st) => ({
      ...st,
      deleteFile: !state.deleteFile,
      fileToDelete: file,
    }));
  };

  useEffect(() => {
    loadMore();
  }, []);

  const loadMore = async () => {
    if (!state.isLoading) {
      const { page, savedFiles } = state;
      setState((st) => ({ ...st, isLoading: true }));
      const files = await broadcast.getSavedBroadcastFiles(userType, page);
      if (files?.length > 0) {
        const list = [...savedFiles, ...files];
        setState((st) => ({
          ...st,
          isLoading: false,
          savedFiles: list,
          page: page + 1,
        }));
      } else {
        setState((st) => ({
          ...st,
          isLoading: false,
          hasMore: false,
        }));
      }
    }
  };

  return (
    <>
      {state.previewPopup && (
        <PreviewFile
          isVisible={state.previewPopup}
          handlePreview={handlePreview}
          fileForPreview={state.fileForPreview}
          handleUseCustomFile={handleUseCustomFile}
          userType={userType}
          editDeleteAccess={editDeleteAccess}
          refreshData={refreshData}
        />
      )}
      {state.deleteFile && (
        <ConfirmPopup
          isDelete
          titleMessage="Confirm to Delete"
          handleCancel={() =>
            setState((st) => ({
              ...st,
              deleteFile: !state.deleteFile,
            }))
          }
          handleDelete={() => Delete(state.fileToDelete)}
          isVisible={state.deleteFile}
          submitButtonText="Delete"
          cancelButtonText="Cancel"
          message={
            <>
              Are you sure you want to delete{" "}
              <strong> {state.fileToDelete.fileNickName} </strong>
              from saved files?
            </>
          }
        />
      )}
      <InfiniteScroll
        loadMore={loadMore}
        hasMore={state.hasMore}
        loader={
          state.isLoading && (
            <LoadingSpinner
              type="small"
              relative
              customClassName="without-background-transparent"
            />
          )
        }
        threshold={5}
        initialLoad={false}
        useWindow={false}
      >
        <div className="hide-for-mob">
          <SelectFileInner
            savedFiles={state.savedFiles}
            edit={state.edit}
            selectedIndex={state.selectedIndex}
            handleOnChange={handleOnChange}
            handleCancel={handleCancel}
            handleSave={handleSave}
            handlePreview={handlePreview}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            selectFile={selectFile}
            isLoading={state.isLoading}
            editDeleteAccess={editDeleteAccess}
          />
        </div>
        {!state.previewPopup && (
          <div className="display-in-mob">
            <SelectFileMob
              savedFiles={state.savedFiles}
              edit={state.edit}
              selectedIndex={state.selectedIndex}
              handleOnChange={handleOnChange}
              handleCancel={handleCancel}
              handleSave={handleSave}
              handlePreview={handlePreview}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
              selectFile={selectFile}
              isLoading={state.isLoading}
              editDeleteAccess={editDeleteAccess}
              handleSelect={props.handleMobFileSelect}
            />
          </div>
        )}
      </InfiniteScroll>
    </>
  );
});

export default SelectFile;
